/** Left rail: saved searches (built-in + yours, with counts) and the project tree. */
import React, { useState } from 'react';
import { ChevronDown, ChevronRight, FolderKanban, PenLine, Plus, Search, Star, Trash2, User, Users } from 'lucide-react';
import {
  type TaskMeta,
  type TaskProject,
  useCreateProjectMutation,
  useDeleteSearchMutation,
} from '@/client/api/tasks/tasks';

const BUILTIN_ICON: Record<string, React.ReactNode> = {
  todo: <Star className="h-3.5 w-3.5 fill-white text-white" />,
  owner: <User className="h-3.5 w-3.5 text-white" />,
  creator: <PenLine className="h-3.5 w-3.5 text-white" />,
  subscriber: <Users className="h-3.5 w-3.5 text-white" />,
};
const BUILTIN_BG: Record<string, string> = {
  todo: 'bg-amber-400',
  owner: 'bg-red-500',
  creator: 'bg-violet-500',
  subscriber: 'bg-blue-600',
};
const PROJECT_BG = ['bg-rose-500', 'bg-sky-500', 'bg-emerald-500', 'bg-violet-500', 'bg-orange-500', 'bg-teal-500', 'bg-indigo-500'];

const Count: React.FC<{ n: number; strong?: boolean }> = ({ n, strong }) =>
  n > 0 ? (
    <span className={`ml-auto rounded px-1.5 text-xs font-semibold ${strong ? 'bg-blue-600 text-white' : 'text-gray-500'}`}>{n}</span>
  ) : null;

const Item: React.FC<{
  active: boolean;
  onClick: () => void;
  icon: React.ReactNode;
  children: React.ReactNode;
  depth?: number;
  extra?: React.ReactNode;
}> = ({ active, onClick, icon, children, depth = 0, extra }) => (
  <div className={`group flex items-center rounded-md ${active ? 'bg-gray-200/80' : 'hover:bg-gray-100'}`} style={{ paddingLeft: depth * 14 }}>
    <button type="button" onClick={onClick} className="flex min-w-0 flex-1 items-center gap-2 px-2 py-1.5 text-left text-sm text-gray-800">
      {icon}
      <span className={`min-w-0 flex-1 truncate ${active ? 'font-semibold' : ''}`}>{children}</span>
    </button>
    {extra}
  </div>
);

export interface SidebarSelection {
  search?: string;
  project?: string;
}

const TaskSidebar: React.FC<{
  meta: TaskMeta | undefined;
  selection: SidebarSelection;
  onSelect: (s: SidebarSelection) => void;
  onSaveSearch: () => void;
}> = ({ meta, selection, onSelect, onSaveSearch }) => {
  const [hideSearches, setHideSearches] = useState(false);
  const [open, setOpen] = useState<Record<string, boolean>>({});
  const [adding, setAdding] = useState<{ parentId: string | null } | null>(null);
  const [name, setName] = useState('');
  const createProject = useCreateProjectMutation();
  const deleteSearch = useDeleteSearchMutation();

  const projects = (meta?.projects ?? []).filter((p) => !p.archived);
  const children = (id: string | null) => projects.filter((p) => p.parentId === id);
  const total = (p: TaskProject): number => p.openCount + children(p.id).reduce((n, c) => n + total(c), 0);

  const addProject = async () => {
    if (!name.trim() || !adding) return setAdding(null);
    const created = await createProject.mutateAsync({ name: name.trim(), parentId: adding.parentId });
    setName('');
    setAdding(null);
    onSelect({ project: created.id });
  };

  const renderProject = (p: TaskProject, depth: number, i: number): React.ReactNode => {
    const kids = children(p.id);
    const expanded = open[p.id] ?? depth === 0;
    return (
      <React.Fragment key={p.id}>
        <Item
          depth={depth}
          active={selection.project === p.id && !selection.search}
          onClick={() => onSelect({ project: p.id })}
          icon={
            <span className="flex items-center gap-1">
              {kids.length > 0 ? (
                <span
                  role="button"
                  tabIndex={0}
                  aria-label={expanded ? 'Collapse' : 'Expand'}
                  onClick={(e) => {
                    e.stopPropagation();
                    setOpen((o) => ({ ...o, [p.id]: !expanded }));
                  }}
                  onKeyDown={(e) => e.key === 'Enter' && setOpen((o) => ({ ...o, [p.id]: !expanded }))}
                  className="text-gray-400"
                >
                  {expanded ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />}
                </span>
              ) : (
                <span className="w-3.5" />
              )}
              <span className={`flex h-5 w-5 items-center justify-center rounded ${PROJECT_BG[i % PROJECT_BG.length]}`}>
                <FolderKanban className="h-3 w-3 text-white" />
              </span>
            </span>
          }
          extra={
            <>
              <button
                type="button"
                aria-label={`New sub-project in ${p.name}`}
                title="New sub-project"
                onClick={() => setAdding({ parentId: p.id })}
                className="hidden px-1 text-gray-400 hover:text-gray-700 group-hover:block"
              >
                <Plus className="h-3.5 w-3.5" />
              </button>
              <span className="pr-2"><Count n={total(p)} strong /></span>
            </>
          }
        >
          {p.name}
        </Item>
        {expanded && kids.map((k, j) => renderProject(k, depth + 1, i + j + 1))}
        {adding?.parentId === p.id && <NewProjectInput depth={depth + 1} />}
      </React.Fragment>
    );
  };

  // eslint-disable-next-line react/no-unstable-nested-components
  function NewProjectInput({ depth }: { depth: number }) {
    return (
      <div style={{ paddingLeft: depth * 14 + 8 }} className="py-1 pr-2">
        <input
          autoFocus
          aria-label="Project name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') void addProject();
            if (e.key === 'Escape') setAdding(null);
          }}
          onBlur={() => void addProject()}
          placeholder="Project name"
          className="h-7 w-full rounded border border-blue-300 px-2 text-sm outline-none"
        />
      </div>
    );
  }

  return (
    <nav aria-label="Tasks navigation" className="flex h-full flex-col overflow-y-auto bg-gray-50 px-2 py-3">
      <div className="flex items-center justify-between px-2 pb-1 text-[11px] font-semibold uppercase tracking-wide text-gray-500">
        Saved searches
        <button type="button" className="font-normal normal-case text-gray-400 hover:text-gray-700" onClick={() => setHideSearches((h) => !h)}>
          {hideSearches ? 'Show' : 'Hide'}
        </button>
      </div>
      {!hideSearches && (
        <div className="grid gap-0.5">
          {(meta?.searches ?? []).map((s) => (
            <Item
              key={s.id}
              active={selection.search === s.id}
              onClick={() => onSelect({ search: s.id })}
              icon={
                <span className={`flex h-5 w-5 items-center justify-center rounded ${BUILTIN_BG[s.id] ?? 'bg-gray-500'}`}>
                  {BUILTIN_ICON[s.id] ?? <Search className="h-3 w-3 text-white" />}
                </span>
              }
              extra={
                <>
                  {s.saved && (
                    <button
                      type="button"
                      aria-label={`Delete saved search ${s.name}`}
                      onClick={() => window.confirm(`Delete "${s.name}"?`) && deleteSearch.mutate(s.id)}
                      className="hidden px-1 text-gray-400 hover:text-red-600 group-hover:block"
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  )}
                  <span className="pr-2"><Count n={s.count} strong={s.id === 'todo'} /></span>
                </>
              }
            >
              {s.name}
            </Item>
          ))}
          <button type="button" onClick={onSaveSearch} className="flex items-center gap-2 px-2 py-1.5 text-sm text-gray-600 hover:text-gray-900">
            <Plus className="h-4 w-4 rounded-full border border-gray-400 p-0.5" /> New Saved Search
          </button>
        </div>
      )}

      <div className="mt-4 px-2 pb-1 text-[11px] font-semibold uppercase tracking-wide text-gray-500">Projects</div>
      <div className="grid gap-0.5">
        {children(null).map((p, i) => renderProject(p, 0, i))}
        {adding?.parentId === null && <NewProjectInput depth={0} />}
        <button type="button" onClick={() => setAdding({ parentId: null })} className="flex items-center gap-2 px-2 py-1.5 text-sm text-gray-600 hover:text-gray-900">
          <Plus className="h-4 w-4 rounded-full border border-gray-400 p-0.5" /> New Project or Program
        </button>
      </div>
    </nav>
  );
};

export default TaskSidebar;
